import { Link } from "react-router-dom";
import { useState, type FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const featuredArticles = articles.filter((a) => a.featured);
const editorPicks = articles.filter((a) => a.editorPick);
const latestArticles = articles.slice(0, 6);

export default function Home() {
  const [subStatus, setSubStatus] = useState<"idle" | "success" | "error">("idle");
  const [subMessage, setSubMessage] = useState("");

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setSubStatus("success");
      setSubMessage("感谢订阅！我们会在每周五发送精选内容到您的邮箱。");
      form.reset();
      return;
    }
    formData.delete("phone_alt");

    try {
      const res = await fetch("https://readdy.ai/api/form/d9tcqe3pv18futdmibd0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (res.ok && parsed?.code === "OK") {
        setSubStatus("success");
        setSubMessage("感谢订阅！我们会在每周五发送精选内容到您的邮箱。");
        form.reset();
      } else {
        setSubStatus("error");
        setSubMessage(serverMsg || "订阅失败，请稍后再试。");
      }
    } catch {
      setSubStatus("error");
      setSubMessage("网络错误，请检查您的网络连接后重试。");
    }
  };

  return (
    <>
      <SchemaOrg
        type="WebSite"
        data={{
          name: "探花精选",
          url: "https://pkgogo.cn",
          description: "探花精选为您甄选最优质的内容与资源，汇聚精品推荐、深度评测与独家发现。",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://pkgogo.cn/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: "探花精选",
          url: "https://pkgogo.cn",
          description: "精品内容一站汇聚",
          sameAs: ["https://weibo.com/tanhuajingxuan"],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative h-[500px] md:h-[620px] flex items-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20reading%20room%20with%20warm%20amber%20lighting%2C%20tall%20wooden%20bookshelves%20filled%20with%20books%2C%20a%20comfortable%20armchair%20by%20a%20large%20window%2C%20soft%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20vintage%20aesthetic%2C%20peaceful%20and%20inviting%20atmosphere%2C%20warm%20earth%20tones%20with%20cream%20and%20terracotta%20accents&width=1800&height=900&seq=home-hero-bg&orientation=landscape"
              alt="探花精选 - 精品内容一站汇聚"
              title="探花精选 - 精品内容一站汇聚"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
            <div className="relative z-10 w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 text-sm mb-6">
                  <i className="ri-sparkling-line"></i>
                  <span>每日精选 · 品质推荐</span>
                </div>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">
                  探花精选
                </h1>
                <p className="mt-4 text-lg md:text-xl text-background-50/90 leading-relaxed">
                  精品内容一站汇聚
                </p>
                <p className="mt-3 text-sm md:text-base text-background-50/70 max-w-xl mx-auto leading-relaxed">
                  为您甄选最优质的内容与资源，汇聚精品推荐、深度评测与独家发现，节省筛选时间，直达高价值信息
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/category/jingxuan-tuijian"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-star-line"></i>
                    浏览精选内容
                  </Link>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 font-medium hover:bg-background-50/25 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-information-line"></i>
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  内容分类
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-2 p-4 md:p-5 rounded-lg border border-background-200/70 hover:border-primary-200 bg-background-50 transition-all duration-300"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-100 group-hover:bg-primary-50 transition-colors">
                      <i className={`${cat.icon} text-lg md:text-xl text-foreground-600 group-hover:text-primary-500 transition-colors`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  精选推荐
                </h2>
                <Link
                  to="/category/jingxuan-tuijian"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {featuredArticles.slice(0, 3).map((article, index) => (
                  <ArticleCard key={article.id} article={article} size={index === 0 ? "large" : "medium"} />
                ))}
              </div>
            </div>
          </section>

          {/* Editor's Picks */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  编辑精选
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {editorPicks.map((article) => (
                  <ArticleCard key={article.id} article={article} size="small" />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                最新内容
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} size="medium" />
                ))}
              </div>
              <div className="mt-8 text-center">
                <Link
                  to="/category/jingxuan-tuijian"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors font-medium whitespace-nowrap"
                >
                  浏览更多内容
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* Newsletter */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-50 mx-auto mb-5">
                <i className="ri-mail-line text-2xl text-primary-500"></i>
              </div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                订阅探花精选
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mb-6 leading-relaxed">
                每周精选内容汇总，不错过任何优质推荐
              </p>
              <form
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                data-readdy-form="true"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="请输入您的邮箱"
                  className="flex-1 px-4 py-3 rounded-full border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  required
                />
                <input
                  type="text"
                  name="phone_alt"
                  className="website-alt-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  立即订阅
                </button>
              </form>
              {subStatus !== "idle" && (
                <p className={`mt-4 text-sm ${subStatus === "success" ? "text-secondary-600" : "text-accent-600"}`}>
                  {subMessage}
                </p>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}